import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import { frameThickness } from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

/** Template5 Layout: [LeftFrame][LeftPanel][DoubleDoors][RightPanel][RightFrame] */
function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const topFrame = getParam(params, 'top_frame_profile')
  const leftPanel = getParam(params, 'left_glass_panel')
  const topCenterPanel = getParam(params, 'top_center_glass_panel')
  const leftDoorPanel = getParam(params, 'left_door_glass_panel')
  const rightDoorPanel = getParam(params, 'right_door_glass_panel')
  const rightPanel = getParam(params, 'right_glass_panel')

  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width
  const topY = topFrame.height

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', {
      position: { x: currentX, y: topY, z: leftPanel.position?.z || 0 },
    })
    currentX += leftPanel.width
  }

  if (topCenterPanel) {
    updateParam(model, 'top_center_glass_panel', {
      position: { x: currentX, y: topY, z: topCenterPanel.position?.z || 0 },
    })
  }

  if (leftDoorPanel && topCenterPanel) {
    updateParam(model, 'left_door_glass_panel', {
      position: { x: currentX, y: topY + topCenterPanel.height, z: leftDoorPanel.position?.z || 0 },
    })
  }

  if (rightDoorPanel && leftDoorPanel && topCenterPanel) {
    currentX += leftDoorPanel.width
    updateParam(model, 'right_door_glass_panel', {
      position: { x: currentX, y: topY + topCenterPanel.height, z: rightDoorPanel.position?.z || 0 },
    })
    currentX += rightDoorPanel.width
  }

  if (rightPanel) {
    updateParam(model, 'right_glass_panel', {
      position: { x: currentX, y: topY, z: rightPanel.position?.z || 0 },
    })
  }
}

function updateDoorHardwarePositions(model: ModelData, doorPanelHeight: number, doorPanelId: string) {
  const doorPanel = getParam(model.parameters!, doorPanelId)
  if (!doorPanel?.hardware) return

  const oldHeight = doorPanel.height
  const updatedHardware = { ...doorPanel.hardware }

  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => {
      const ratio = oldHeight > 0 ? lock.position.y / oldHeight : 0.9
      return { ...lock, position: { ...lock.position, y: doorPanelHeight * ratio } }
    })
  }

  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.y < 0) return hinge
      const ratio = oldHeight > 0 ? hinge.position.y / oldHeight : 0.9
      return { ...hinge, position: { ...hinge.position, y: doorPanelHeight * ratio } }
    })
  }

  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => {
      const ratio = oldHeight > 0 ? handle.position.y / oldHeight : 0.5
      return { ...handle, position: { ...handle.position, y: doorPanelHeight * ratio } }
    })
  }

  updateParam(model, doorPanelId, { hardware: updatedHardware })
}

function updateDoorHardwarePositionsX(model: ModelData, doorPanelWidth: number, doorPanelId: string) {
  const doorPanel = getParam(model.parameters!, doorPanelId)
  if (!doorPanel?.hardware) return

  const oldWidth = doorPanel.width
  const updatedHardware = { ...doorPanel.hardware }

  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => {
      const ratio = oldWidth > 0 ? lock.position.x / oldWidth : 0.9
      return { ...lock, position: { ...lock.position, x: doorPanelWidth * ratio } }
    })
  }

  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => {
      const ratio = oldWidth > 0 ? handle.position.x / oldWidth : 0.5
      return { ...handle, position: { ...handle.position, x: doorPanelWidth * ratio } }
    })
  }

  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.x < 0) {
        const ratio = oldWidth > 0 ? Math.abs(hinge.position.x) / oldWidth : 0.1
        return { ...hinge, position: { ...hinge.position, x: -doorPanelWidth * ratio } }
      }
      const ratio = oldWidth > 0 ? hinge.position.x / oldWidth : 0.9
      return { ...hinge, position: { ...hinge.position, x: doorPanelWidth * ratio } }
    })
  }

  updateParam(model, doorPanelId, { hardware: updatedHardware })
}

const totalWidth: DimensionHandler = (model, _initial, numValue) => {
  const currentTotalWidth = model.width as number
  const widthDifference = numValue - currentTotalWidth

  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  const halfDifference = widthDifference / 2
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')

  if (bottomLeft && bottomRight) {
    updateParam(model, 'bottom_left_frame_profile', { width: bottomLeft.width + halfDifference })
    updateParam(model, 'bottom_right_frame_profile', { width: bottomRight.width + halfDifference })
  }

  const leftPanel = getParam(model.parameters!, 'left_glass_panel')
  const rightPanel = getParam(model.parameters!, 'right_glass_panel')

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', { width: leftPanel.width + halfDifference })
  }
  if (rightPanel) {
    updateParam(model, 'right_glass_panel', { width: rightPanel.width + halfDifference })
  }

  recalculatePositions(model)
  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue) => {
  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  const topCenterPanel = getParam(model.parameters!, 'top_center_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const bottomLeftFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRightFrame = getParam(model.parameters!, 'bottom_right_frame_profile')

  if (topCenterPanel && topFrame) {
    const remainingHeight = numValue - topFrame.height * 2
    const remainingDoorHeight = numValue - topCenterPanel.height - topFrame.height

    const newBottomY = numValue - frameThickness
    if (bottomLeftFrame) {
      updateParam(model, 'bottom_left_frame_profile', {
        position: { ...bottomLeftFrame.position, y: newBottomY },
      })
    }
    if (bottomRightFrame) {
      updateParam(model, 'bottom_right_frame_profile', {
        position: { ...bottomRightFrame.position, y: newBottomY },
      })
    }

    updateParam(model, 'left_glass_panel', { height: remainingHeight })
    updateParam(model, 'right_glass_panel', { height: remainingHeight })
    updateParam(model, 'left_door_glass_panel', { height: remainingDoorHeight })
    updateParam(model, 'right_door_glass_panel', { height: remainingDoorHeight })

    if (remainingDoorHeight > 0) {
      updateDoorHardwarePositions(model, remainingDoorHeight, 'left_door_glass_panel')
      updateDoorHardwarePositions(model, remainingDoorHeight, 'right_door_glass_panel')
    }
  }

  return model
}

const topCenterHeight: DimensionHandler = (model, _initial, numValue) => {
  const currentTopCenter = getParam(model.parameters!, 'top_center_glass_panel')
  const leftDoorPanel = getParam(model.parameters!, 'left_door_glass_panel')
  const rightDoorPanel = getParam(model.parameters!, 'right_door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!currentTopCenter || !leftDoorPanel || !rightDoorPanel || !topFrame) return model

  const newTopCenterHeight = numValue - topFrame.height
  const newTransomHeight = numValue

  updateParam(model, 'top_center_glass_panel', {
    height: newTopCenterHeight,
    position: { ...currentTopCenter.position, y: frameThickness },
  })

  updateParam(model, 'left_door_glass_panel', {
    position: { ...leftDoorPanel.position, y: newTransomHeight },
  })
  updateParam(model, 'right_door_glass_panel', {
    position: { ...rightDoorPanel.position, y: newTransomHeight },
  })

  const newTotalHeight = newTransomHeight + leftDoorPanel.height
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) {
    updateParam(model, 'left_glass_panel', {
      height: leftFrame.height - frameThickness - frameThickness,
    })
  }
  if (rightFrame) {
    updateParam(model, 'right_glass_panel', {
      height: rightFrame.height - frameThickness - frameThickness,
    })
  }

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: newTotalHeight - frameThickness },
    })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, y: newTotalHeight - frameThickness },
    })
  }

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const leftDoorPanel = getParam(model.parameters!, 'left_door_glass_panel')
  const rightDoorPanel = getParam(model.parameters!, 'right_door_glass_panel')
  const topCenterPanel = getParam(model.parameters!, 'top_center_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!leftDoorPanel || !rightDoorPanel || !topCenterPanel || !topFrame) return model

  updateParam(model, 'left_door_glass_panel', { height: numValue })
  updateParam(model, 'right_door_glass_panel', { height: numValue })

  const newTotalHeight = topFrame.height + topCenterPanel.height + numValue
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) {
    updateParam(model, 'left_glass_panel', {
      height: leftFrame.height - frameThickness - frameThickness,
    })
  }
  if (rightFrame) {
    updateParam(model, 'right_glass_panel', {
      height: rightFrame.height - frameThickness - frameThickness,
    })
  }

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: newTotalHeight - frameThickness },
    })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, y: newTotalHeight - frameThickness },
    })
  }

  if (leftDoorPanel.hardware) {
    updateDoorHardwarePositions(model, numValue, 'left_door_glass_panel')
  }
  if (rightDoorPanel.hardware) {
    updateDoorHardwarePositions(model, numValue, 'right_door_glass_panel')
  }

  return model
}

const centerSectionWidth: DimensionHandler = (model, _initial, numValue) => {
  const leftDoorPanel = getParam(model.parameters!, 'left_door_glass_panel')
  const rightDoorPanel = getParam(model.parameters!, 'right_door_glass_panel')
  const currentCenterWidth = (leftDoorPanel?.width || 0) + (rightDoorPanel?.width || 0)
  const widthDifference = numValue - currentCenterWidth
  const halfDifference = widthDifference / 2

  const newLeftDoorWidth = (leftDoorPanel?.width || 0) + halfDifference
  const newRightDoorWidth = (rightDoorPanel?.width || 0) + halfDifference

  updateParam(model, 'left_door_glass_panel', { width: newLeftDoorWidth })
  updateParam(model, 'right_door_glass_panel', { width: newRightDoorWidth })
  updateParam(model, 'top_center_glass_panel', { width: numValue })
  updateParam(model, 'bottom_center_frame_profile', { width: numValue })

  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: numValue,
    individualDoorWidth: [newLeftDoorWidth, newRightDoorWidth],
  }

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  if (newLeftDoorWidth > 0) {
    updateDoorHardwarePositionsX(model, newLeftDoorWidth, 'left_door_glass_panel')
  }
  if (newRightDoorWidth > 0) {
    updateDoorHardwarePositionsX(model, newRightDoorWidth, 'right_door_glass_panel')
  }

  recalculatePositions(model)
  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!bottomLeft || !leftFrame) return model

  const widthDifference = numValue - bottomLeft.width

  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  updateParam(model, 'left_glass_panel', { width: numValue - leftFrame.width })

  if (topFrame) {
    const bottomY = (model.height as number) - topFrame.height
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: bottomY },
    })
  }

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  recalculatePositions(model)
  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!bottomRight || !rightFrame) return model

  const widthDifference = numValue - bottomRight.width

  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  updateParam(model, 'right_glass_panel', { width: numValue - rightFrame.width })

  if (topFrame) {
    const bottomY = (model.height as number) - topFrame.height
    const bottomX = (model.width as number) - numValue
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, x: bottomX, y: bottomY },
    })
  }

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  recalculatePositions(model)
  return model
}

const parameterWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId, cascadeUpdates = true } = options as { positionId?: string; cascadeUpdates?: boolean }
  if (!positionId) return model

  const currentParameter = getParam(model.parameters!, positionId as string)
  if (!currentParameter) return model

  const currentWidth = currentParameter.width
  const widthDifference = numValue - currentWidth
  updateParam(model, positionId as string, { width: numValue })

  if (cascadeUpdates) {
    if (positionId === 'left_door_glass_panel' || positionId === 'right_door_glass_panel') {
      const leftDoor = getParam(model.parameters!, 'left_door_glass_panel')
      const rightDoor = getParam(model.parameters!, 'right_door_glass_panel')

      if (leftDoor && rightDoor) {
        const newTotalDoorWidth = leftDoor.width + rightDoor.width
        model.doorConfiguration = {
          ...model.doorConfiguration!,
          totalDoorWidth: newTotalDoorWidth,
          individualDoorWidth: [leftDoor.width, rightDoor.width],
        }
        updateParam(model, 'top_center_glass_panel', { width: newTotalDoorWidth })
        updateParam(model, 'bottom_center_frame_profile', { width: newTotalDoorWidth })

        model.width = (model.width as number) + widthDifference
        updateParam(model, 'top_frame_profile', { width: model.width as number })

        updateDoorHardwarePositionsX(model, numValue, positionId as string)
        recalculatePositions(model)
      }
    } else if (positionId === 'top_center_glass_panel') {
      const leftDoor = getParam(model.parameters!, 'left_door_glass_panel')
      const rightDoor = getParam(model.parameters!, 'right_door_glass_panel')

      if (leftDoor && rightDoor) {
        const halfDifference = widthDifference / 2
        const newLeftWidth = leftDoor.width + halfDifference
        const newRightWidth = rightDoor.width + halfDifference

        updateParam(model, 'left_door_glass_panel', { width: newLeftWidth })
        updateParam(model, 'right_door_glass_panel', { width: newRightWidth })

        model.doorConfiguration = {
          ...model.doorConfiguration!,
          totalDoorWidth: numValue,
          individualDoorWidth: [newLeftWidth, newRightWidth],
        }
        updateParam(model, 'bottom_center_frame_profile', { width: numValue })

        updateDoorHardwarePositionsX(model, newLeftWidth, 'left_door_glass_panel')
        updateDoorHardwarePositionsX(model, newRightWidth, 'right_door_glass_panel')
        recalculatePositions(model)
      }
    } else {
      recalculatePositions(model)
    }
  }

  return model
}

export const model5DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  topCenterHeight,
  doorPanelHeight,
  centerSectionWidth,
  leftFrameWidth,
  rightFrameWidth,
  parameterWidth,
}
